/**
 * Custom element that toggles the announcement bar between
 * the default marquee and a free-shipping progress bar.
 *
 * Intercepts fetch calls to cart API endpoints so we catch
 * ALL cart modifications (add, change, update, remove, clear).
 */
class AnnouncementShippingBar extends HTMLElement {
  connectedCallback() {
    this.threshold = parseInt(this.dataset.threshold, 10) || 0;
    this.successText = this.dataset.successText || '';
    this.underText = this.dataset.underText || '';
    this.marqueeEl = this.querySelector('[data-announcement-marquee]');
    this.shippingEl = this.querySelector('[data-announcement-shipping]');
    this.messageEl = this.querySelector('[data-shipping-message]');
    this.meterEl = this.querySelector('[data-shipping-meter]');

    if (!this.threshold || !this.marqueeEl || !this.shippingEl) return;

    // Check initial cart state
    this.#fetchCartAndUpdate(false);

    // Listen for theme cart events
    document.addEventListener('cart:update', this.#onCartUpdate);

    // Intercept fetch to catch all cart API calls (quantity changes, removals, etc.)
    this.#patchFetch();
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this.#onCartUpdate);
    this.#restoreFetch();
  }

  #originalFetch = null;

  #patchFetch() {
    this.#originalFetch = window.fetch;
    const self = this;

    window.fetch = function (...args) {
      const result = self.#originalFetch.apply(this, args);
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';

      if (
        url.includes('/cart/add') ||
        url.includes('/cart/change') ||
        url.includes('/cart/update') ||
        url.includes('/cart/clear')
      ) {
        result
          .then((response) => response.clone().json())
          .then(() => {
            // Small delay to ensure Shopify has committed the cart change
            setTimeout(() => self.#fetchCartAndUpdate(true), 150);
          })
          .catch(() => {});
      }

      return result;
    };
  }

  #restoreFetch() {
    if (this.#originalFetch) {
      window.fetch = this.#originalFetch;
    }
  }

  #onCartUpdate = () => {
    this.#fetchCartAndUpdate(true);
  };

  #fetchPending = false;

  async #fetchCartAndUpdate(animate) {
    // Debounce: skip if a fetch is already in-flight
    if (this.#fetchPending) return;
    this.#fetchPending = true;

    try {
      const res = await fetch('/cart.js', { cache: 'no-store' });
      const cart = await res.json();
      this.#update(cart, animate);
    } catch (e) {
      // On error, keep current state
    } finally {
      this.#fetchPending = false;
    }
  }

  #update(cart, animate) {
    const hasItems = cart.item_count > 0;

    if (hasItems) {
      this.#showShipping(cart, animate);
    } else {
      this.#showMarquee(animate);
    }
  }

  #showShipping(cart, animate) {
    const percentage = Math.min((cart.total_price / this.threshold) * 100, 100);
    const isSuccess = cart.total_price >= this.threshold;

    // Update message
    if (this.messageEl) {
      if (isSuccess) {
        this.messageEl.textContent = this.successText;
      } else {
        const remaining = this.threshold - cart.total_price;
        const formattedAmount = this.#formatMoney(remaining);
        this.messageEl.textContent = this.underText.replace('[amount]', formattedAmount);
      }
    }

    // Update meter
    if (this.meterEl) {
      this.meterEl.style.width = percentage + '%';
    }

    // Toggle success class for color change
    if (this.shippingEl) {
      this.shippingEl.classList.toggle('announcement-shipping--success', isSuccess);
    }

    // Swap visibility
    if (animate) {
      this.marqueeEl.classList.add('announcement-state--hidden');
      this.shippingEl.classList.remove('announcement-state--hidden');
      this.shippingEl.classList.add('announcement-state--visible');
    } else {
      this.marqueeEl.style.display = 'none';
      this.marqueeEl.classList.add('announcement-state--hidden');
      this.shippingEl.classList.remove('announcement-state--hidden');
      this.shippingEl.classList.add('announcement-state--visible');
      this.shippingEl.style.display = '';
    }
  }

  #showMarquee(animate) {
    if (animate) {
      this.shippingEl.classList.add('announcement-state--hidden');
      this.shippingEl.classList.remove('announcement-state--visible');
      this.marqueeEl.classList.remove('announcement-state--hidden');
      this.marqueeEl.style.display = '';
    } else {
      this.marqueeEl.style.display = '';
      this.marqueeEl.classList.remove('announcement-state--hidden');
      this.shippingEl.classList.add('announcement-state--hidden');
      this.shippingEl.classList.remove('announcement-state--visible');
    }
  }

  #formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    // Remove trailing .00
    const clean = amount.replace(/\.00$/, '');
    return '$' + clean;
  }
}

if (!customElements.get('announcement-shipping-bar')) {
  customElements.define('announcement-shipping-bar', AnnouncementShippingBar);
}
